import {
  BaseNotificationFragment,
  CoralCommentFragment,
  CoralReplyFragment,
  CoralSiteFragment,
  CoralStoryFragment,
  CycloneCommunityFragment,
  CycloneReplyFragment,
  CycloneFanPostFragment,
  CycloneExternalRootFragment,
  UserWithProfilesFragment,
} from "@framework/notifications/graphql/types";
import { makeRequest } from "@framework/notifications/request";
import {
  AnyNotification,
  NotificationsFilter,
  NotificationsSort,
} from "@voxmedia/duet-context/context/notifications-context";

export const DEFAULT_PAGINATION_COUNT = 20;

export interface NotificationsPagination {
  items: AnyNotification[];
  hasNext: boolean;
  nextCursor?: string | null;
}

interface NotificationsForUserResponse {
  forUser: NotificationsPagination;
}

const query = /* GraphQL */ `
  query FetchNotificationsForUser($cursor: String, $count: Int!, $filter: NotificationsFilter, $sort: NotificationsSort) {
    forUser(cursor: $cursor, count: $count, filter: $filter, sort: $sort) {
      hasNext
      nextCursor
      items {
        ${BaseNotificationFragment}

        ... on CycloneReplyNotification {
          from {
            ${UserWithProfilesFragment}
          }
          to {
            ${UserWithProfilesFragment}
          }
          parent {
            ${CycloneReplyFragment}
          }
          reply {
            ${CycloneReplyFragment}
          }
          fanPost {
            ${CycloneFanPostFragment}
          }
          community {
            ${CycloneCommunityFragment}
          }
          rootAncestor {
            ... on CycloneFanPost {
              ${CycloneFanPostFragment}
            }
            ... on CycloneExternalRoot {
              ${CycloneExternalRootFragment}
            }
          }
        }

        ... on CoralReplyNotification {
          from {
            ${UserWithProfilesFragment}
          }
          to {
            ${UserWithProfilesFragment}
          }
          story {
            ${CoralStoryFragment}
          }
          site {
            ${CoralSiteFragment}
          }
          comment {
            ${CoralCommentFragment}
          }
          reply {
            ${CoralReplyFragment}
          }
        }

        ... on CycloneFanPostReplyNotification {
          from {
            ${UserWithProfilesFragment}
          }
          to {
            ${UserWithProfilesFragment}
          }
          fanPost {
            ${CycloneFanPostFragment}
          }
          reply {
            ${CycloneReplyFragment}
          }
          community {
            ${CycloneCommunityFragment}
          }
        }

        ... on CoralRecSummaryNotification {
          updatedAt
          to {
            ${UserWithProfilesFragment}
          }
          recs {
            ${BaseNotificationFragment}
            ... on CoralRecNotification {
              from {
                ${UserWithProfilesFragment}
              }
            }
          }
          story {
            ${CoralStoryFragment}
          }
          site {
            ${CoralSiteFragment}
          }
          comment {
            ${CoralCommentFragment}
          }
        }

        ... on CycloneFanPostRecSummaryNotification {
          updatedAt
          to {
            ${UserWithProfilesFragment}
          }
          recs {
            ${BaseNotificationFragment}
            ... on CycloneFanPostRecNotification {
              from {
                ${UserWithProfilesFragment}
              }
            }
          }
          fanPost {
            ${CycloneFanPostFragment}
          }
          community {
            ${CycloneCommunityFragment}
          }
        }

        ... on CycloneReplyRecSummaryNotification {
          updatedAt
          to {
            ${UserWithProfilesFragment}
          }
          recs {
            ${BaseNotificationFragment}
            ... on CycloneReplyRecNotification {
              from {
                ${UserWithProfilesFragment}
              }
            }
          }
          reply {
            ${CycloneReplyFragment}
          }
          community {
            ${CycloneCommunityFragment}
          }
          rootAncestor {
            ... on CycloneFanPost {
              ${CycloneFanPostFragment}
            }
            ... on CycloneExternalRoot {
              ${CycloneExternalRootFragment}
            }
          }
        }

        ... on CoralCommentWasApprovedNotification {
          to {
            ${UserWithProfilesFragment}
          }
          comment {
            ${CoralCommentFragment}
          }
          site {
            ${CoralSiteFragment}
          }
          story {
            ${CoralStoryFragment}
          }
        }

        ... on CycloneFanPostWasApprovedNotification {
          to {
            ${UserWithProfilesFragment}
          }
          fanPost {
            ${CycloneFanPostFragment}
          }
          community {
            ${CycloneCommunityFragment}
          }
        }

        ... on CycloneReplyWasApprovedNotification {
          to {
            ${UserWithProfilesFragment}
          }
          reply {
            ${CycloneReplyFragment}
          }
          community {
            ${CycloneCommunityFragment}
          }
        }
      }
    }
  }
`;

/**
 * Fetch notifications for a user from the Notifications service.
 *
 * @param serverURL - Base URL of the Notifications service
 * @param userID - The Firebase userID to fetch notifications for
 * @param cursor - Optional pagination cursor returned from previous call
 * @param token - Optional bearer token for authenticated requests
 * @param count - Optional number of notifications to fetch
 * @param filter - Optional filter to apply to the notifications
 * @param sort - Optional sort to apply to the notifications
 */
export const fetchNotificationsForUser = async (params: {
  token: string;
  cursor?: string;
  serverURL?: string;
  count?: number;
  filter?: NotificationsFilter;
  sort?: NotificationsSort;
}): Promise<NotificationsPagination> => {
  const {
    cursor,
    token,
    serverURL = process.env.NEXT_PUBLIC_NOTIFICATIONS_SERVICE_URL,
    count = DEFAULT_PAGINATION_COUNT,
    filter,
    sort,
  } = params;

  if (!serverURL) {
    throw new Error("Missing config value for Notifications Service URL.");
  }

  const apiURL = new URL("/api", serverURL).toString();

  const response = await makeRequest<NotificationsForUserResponse>(
    apiURL,
    query,
    {
      cursor,
      count,
      filter,
      sort,
    },
    token,
  );

  return response.data?.forUser || { items: [], hasNext: false };
};
